import { Controller } from "@hotwired/stimulus"
import { i18n } from "../../lib/i18n"

// DEAD CODE — kept in tree for easy revert. Mounted Stripe.js's embedded
// Checkout iframe, which we replaced with the pay-later disclosure flow.
// Only `app/views/onboarding/checkout_sessions/new.html.erb` references this
// controller, and that view is no longer rendered from any user path.
export default class extends Controller {
  static targets = ["checkout"]
  static values = { 
    publishableKey: String,
    config: Object
  }

  declare checkout: any
  declare stripe: any
  declare sessionId: string

  connect() {
    this.initializeStripe()
  }

  disconnect() {
    // Ensure previously mounted embedded checkout is torn down
    try {
      if (this.checkout && typeof this.checkout.destroy === 'function') {
        this.checkout.destroy()
      } else if (this.checkout && typeof this.checkout.unmount === 'function') {
        this.checkout.unmount()
      }
    } catch (e) {
      // no-op
    } finally {
      this.checkout = null
      if (this.hasCheckoutTarget) {
        this.checkoutTarget.innerHTML = ''
      }
    }
  }

  async initializeStripe() {
    if (typeof Stripe === 'undefined') {
      console.error('Stripe.js not loaded')
      return
    }

    this.stripe = Stripe(this.publishableKeyValue)
    await this.createCheckoutSession()
  }

  async createCheckoutSession() {
    try {
      // Defensive: if a previous instance exists, tear it down before creating a new one
      if (this.checkout) {
        try {
          if (typeof this.checkout.destroy === 'function') {
            this.checkout.destroy()
          } else if (typeof this.checkout.unmount === 'function') {
            this.checkout.unmount()
          }
        } catch (_) {}
        this.checkout = null
      }

      const config = this.configValue || {}
      const body: any = {
        network_token: config.network_token,
        price_id: config.price_id || undefined,
        lookup_key: config.lookup_key || undefined,
        user_count: config.user_count || undefined,
        promo_code: config.promo_code || undefined
      }

      const response = await fetch('/checkout_session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify(body)
      })

      const data = await response.json()

      if (data.error) {
        console.error('Error creating checkout session:', data.error)
        this.showError(data.error)
        return
      }

      this.sessionId = data.session_id
      await this.initializeEmbeddedCheckout(data.client_secret)
    } catch (error) {
      console.error('Error:', error)
      this.showError(i18n.t('onboarding.stripe_checkout.payment_failed'))
    }
  }

  async initializeEmbeddedCheckout(clientSecret: string) {
    try {
      // Clear the checkout target element completely - Stripe requires it to be empty
      this.checkoutTarget.innerHTML = ''

      // The Checkout Session is created with `redirect_on_completion: 'never'`
      // because our app sends X-Frame-Options: DENY (HIPAA), which would prevent
      // Stripe's iframe from rendering our return_url. Instead we navigate the
      // PARENT window from the onComplete callback below.
      this.checkout = await this.stripe.initEmbeddedCheckout({
        clientSecret,
        onComplete: () => this.handleComplete()
      })
      this.checkout.mount(this.checkoutTarget)
    } catch (error) {
      console.error('Error mounting checkout:', error)
      this.showError(i18n.t('onboarding.stripe_checkout.payment_error'))
    }
  }

  handleComplete() {
    if (!this.sessionId) {
      console.error('Missing Stripe session id; cannot complete checkout flow.')
      return
    }

    const target = window.top || window
    target.location.href = `/checkout_session/complete?stripe_session_id=${encodeURIComponent(this.sessionId)}`
  }

  showError(message: string) {
    this.checkoutTarget.innerHTML = `
      <div class="alert alert-error">
        <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span>${message}</span>
      </div>
    `
  }
}
